import { useEffect, useRef, useState, useCallback } from 'react';
import { getAppVersion } from '../utils/versionUtils';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // Option 3: check every 5 minutes
const VERSION_ENDPOINT = `${process.env.REACT_APP_API_URL || ''}/api/version`;

async function fetchServerVersion(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_ENDPOINT, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Polls the server version every POLL_INTERVAL_MS (option 3) and also checks
 * whenever the browser tab regains focus (option 4).
 *
 * Returns:
 *  - `updateAvailable`: true when server version differs from the built-in version
 *  - `reload`: call this to hard-reload the page and pick up the new build
 */
export function useVersionCheck() {
  const currentVersion = getAppVersion();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    // Skip in dev mode where version is always "dev"
    if (currentVersion === 'dev') return;
    const serverVersion = await fetchServerVersion();
    if (serverVersion && serverVersion !== 'dev' && serverVersion !== currentVersion) {
      setUpdateAvailable(true);
    }
  }, [currentVersion]);

  useEffect(() => {
    // Option 3: periodic polling
    check(); // immediate first check
    timerRef.current = setInterval(check, POLL_INTERVAL_MS);

    // Option 4: check when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
